'use client';

import { HTMLAttributes, ReactNode, createContext, use, useState } from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';

import { Styles, css, cx } from '@flipster/styled-system/css';

import { Navigator } from './navigator';

const CarouselContext = createContext<{ swiperInstance: SwiperClass | null; isBeginning: boolean; isEnd: boolean }>({
  swiperInstance: null,
  isBeginning: false,
  isEnd: false,
});

interface CarouselProps
  extends Pick<
    SwiperOptions,
    | 'initialSlide'
    | 'slidesPerView'
    | 'spaceBetween'
    | 'breakpoints'
    | 'autoplay'
    | 'modules'
    | 'loop'
    | 'effect'
    | 'creativeEffect'
    | 'centeredSlides'
  > {
  title?: ReactNode;
  onInit?: (activeIndex: number, currentBreakpoint: string) => void;
  onChange?: (activeIndex: number, currentBreakpoint: string) => void;
  navigator?: ReactNode;
  children?: ReactNode;
  withOverflow?: boolean;
  css?: Styles;
  className?: string; // TODO 임시 기존 레거시 폰트를 제거 할때까지는 잠시 유지 필요
}

/**
 * @description
 * rolling 적용하는법
 * loop=true
 * autoplay={{delay: number}}
 * modules={[Autoplay]}
 */
const CarouselBase = ({
  title,
  onInit,
  onChange,
  navigator,
  children,
  withOverflow,
  css: cssProps,
  className,
  slidesPerView,
  ...props
}: CarouselProps) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = useState(false);
  const [isEnd, setIsEnd] = useState(false);
  return (
    <CarouselContext value={{ swiperInstance, isBeginning, isEnd }}>
      <section
        className={cx(
          css(
            {
              pos: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gridTemplateRows: 'auto auto auto',
              gridTemplateAreas: `
              "title arrow-right"
              "carousel carousel"
              "arrow-bottom ."
            `,
              w: '100%',
            },
            cssProps,
          ),
          className,
        )}
      >
        {title && <header className={css({ gridArea: 'title' })}>{title}</header>}
        {navigator}
        <Swiper
          className={cx(
            'swiper',
            css({ gridArea: 'carousel', w: '100%' }),
            withOverflow && css({ overflow: 'initial!' }),
          )}
          speed={600}
          pagination={false}
          navigation={false}
          onInit={(swiper) => {
            setSwiperInstance(swiper);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
            onInit?.(swiper.realIndex, swiper.currentBreakpoint);
          }}
          onSlidesUpdated={(swiper) => {
            setIsBeginning(props.loop ? false : swiper.isBeginning);
            setIsEnd(props.loop ? false : swiper.isEnd);
          }}
          // slideChange event는 loop를 돌다가 prevIndex, curIndex 두 번 호출되는 이슈가 있음
          onSlideChangeTransitionStart={(swiper) => {
            setIsBeginning(props.loop ? false : swiper.isBeginning);
            setIsEnd(props.loop ? false : swiper.isEnd);
            onChange?.(swiper.realIndex, swiper.currentBreakpoint);
          }}
          // 슬라이드의 남은 구간이 하나의 너비보다 작은 경우 sliceChange 이벤트가 호출되지 않음
          onToEdge={(swiper) => {
            setIsBeginning(props.loop ? false : swiper.isBeginning);
            setIsEnd(props.loop ? false : swiper.isEnd);
          }}
          slidesPerView={slidesPerView ?? 1}
          {...props}
        >
          {children}
        </Swiper>
      </section>
    </CarouselContext>
  );
};

interface CarouselSlideProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  css?: Styles;
  className?: string; // TODO 임시 기존 레거시 폰트를 제거 할때까지는 잠시 유지 필요
}

const Slide = ({ children, css: cssProps, className, ...props }: CarouselSlideProps) => {
  return (
    <SwiperSlide className={cx(css(cssProps), className)} {...props}>
      {children}
    </SwiperSlide>
  );
};
// hack for isChildSwiperSlide
// see: https://github.com/nolimits4web/swiper/blob/d197641d62a6cdeaf5c791e3dd808bdcd1376e90/src/react/get-children.mjs#L3-L5
Slide.displayName = 'SwiperSlide';

interface CarouselNavigatorProps {
  onPrev?: () => void;
  onNext?: () => void;
  css?: Styles;
}

/**
 * @param css { display: { base: 'flex' | 'none' }, gridArea: { base: 'arrow-right' or 'arrow-bottom' }  }
 * @constructor
 */
const CarouselNavigator = ({ css: cssProps, onPrev, onNext }: CarouselNavigatorProps) => {
  const { swiperInstance, isBeginning, isEnd } = use(CarouselContext);
  return (
    <Navigator
      onPrev={() => {
        onPrev?.();
        swiperInstance?.slidePrev();
      }}
      onNext={() => {
        onNext?.();
        swiperInstance?.slideNext();
      }}
      isPrevDisabled={!swiperInstance || isBeginning}
      isNextDisabled={!swiperInstance || isEnd}
      css={css.raw({ gridArea: 'arrow-right', alignItems: 'flex-start' }, cssProps)}
    />
  );
};

export const rollingProps = { loop: true, autoplay: { delay: 2500 }, modules: [Autoplay] };

CarouselBase.displayName = 'Carousel';
CarouselNavigator.displayName = 'Carousel.Navigator';
export const Carousel = Object.assign(CarouselBase, {
  Slide,
  Navigator: CarouselNavigator,
});

// Export Autoplay module for convenience
export { Autoplay, EffectCreative } from 'swiper/modules';
